import { Button } from "@/src/components/ui/button";
import { Footer } from "@/src/components/ui/footer";
import { Input } from "@/src/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { Content } from "@/src/components/wrappers/Content";
import { ActionsContext, useEmit } from "@/src/lib/actions";
import { useDebounce } from "@/src/lib/useDebounce";
import { GripVerticalIcon, PlusIcon } from "lucide-react";
import { ChangeEvent, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AmenityGroupsTable } from "./GroupsTable";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/src/components/ui/dialog";
import { AmenityGroup } from "./model";
import { Table, TableCell, TableRow } from "@/src/components/ui/table";
import { Sortable, SortableItem, SortableDragHandle } from "@/src/components/ui/sortable";
import { useFetch } from "@/src/lib/useFetch";
import { toast } from "@/src/components/ui/use-toast";

export function Amenities() {
  const [amenityGroups, setAmenityGroups] = useState<AmenityGroup[]>([]);
  const [reorderGroups, setReorderGroups] = useState<AmenityGroup[]>([]);
  const [reorderOpen, setReorderOpen] = useState(false);

  const { fetch, isFetching } = useFetch();

  const fetchAmenityGroups = () =>
    fetch<{ data: AmenityGroup[] }>("/api/amenities/groups").then((r) => {
      setAmenityGroups(r.data);
      setReorderGroups(r.data);
    });

  useEffect(() => {
    fetchAmenityGroups();
  }, []);

  const onChangeOrder = async () => {
    const mapped = reorderGroups.map((g, i) => ({
      ...g,
      order: i + 1,
    }));
    try {
      await fetch("/api/amenities/groups", "PATCH", mapped);
      toast({
        title: "Amenities reordered",
      });
      setAmenityGroups(mapped);
      setReorderGroups(mapped);
    } catch (e: unknown) {
      toast({
        title: "Error",
        description: "An error has occurred while reordering amenities",
        variant: "destructive",
      });
    }
  };

  return (
    <ActionsContext>
      <Content
        title="Amenities"
        contentAction={
          <div className="flex gap-2">
            <Dialog
              open={reorderOpen}
              onOpenChange={(open) => {
                setReorderOpen(open);
                if (open) {
                  setReorderGroups(amenityGroups);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button className="bg-white" variant="outline">
                  Reorder amenities
                </Button>
              </DialogTrigger>
              <DialogContent className="flex flex-col gap-6 max-h-[90vh]">
                <DialogTitle>Reorder amenities</DialogTitle>
                <Table>
                  <Sortable value={reorderGroups} onValueChange={setReorderGroups}>
                    {reorderGroups.map((r) => (
                      <SortableItem value={r.id} asChild key={r.id}>
                        <TableRow>
                          <TableCell>{r.name}</TableCell>
                          <TableCell className="text-right">
                            <SortableDragHandle size="sm" variant="ghost">
                              <GripVerticalIcon size={20} />
                            </SortableDragHandle>
                          </TableCell>
                        </TableRow>
                      </SortableItem>
                    ))}
                  </Sortable>
                </Table>
                <Button disabled={isFetching} onClick={onChangeOrder} className="ml-auto">
                  Save order
                </Button>
              </DialogContent>
            </Dialog>
            <Button asChild>
              <Link to="/amenities/add">
                Add an amenity group
                <PlusIcon className="ml-2" size={16} />
              </Link>
            </Button>
          </div>
        }
      >
        <div className="mb-2 flex justify-start items-center gap-2">
          <SearchInput />
          <PageSizeSelect />
        </div>
        <AmenityGroupsTable amenityGroups={amenityGroups} />
        <Footer />
      </Content>
    </ActionsContext>
  );
}
function SearchInput() {
  const [value, setValue] = useState("");
  const emit = useEmit<string>("am:search");
  const debouncedEmit = useDebounce(emit, 300);

  const onChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const value = ev.currentTarget.value;
    setValue(value);
    debouncedEmit(value);
  };

  return (
    <div className="w-96">
      <Input
        value={value}
        onChange={onChange}
        type="search"
        placeholder="Name filter"
        className="w-full bg-white"
        style={{ padding: "0.5rem" }}
      />
    </div>
  );
}

function PageSizeSelect() {
  const [value, setValue] = useState("10");
  const emit = useEmit<number>("am:page-size");

  const onValueChange = (value: string) => {
    setValue(value);
    emit(parseInt(value));
  };

  return (
    <div className="w-60 ml-auto">
      <Select onValueChange={onValueChange} value={value}>
        <SelectTrigger>
          <SelectValue placeholder="Page size" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="5">Show: 5 at once</SelectItem>
          <SelectItem value="10">Show: 10 at once</SelectItem>
          <SelectItem value="20">Show: 20 at once</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
